/**
 * Strict boundary types for legacy/editor-authored custom tracks.
 *
 * This module deliberately stops at a validated, cloned document.  It does
 * not build a v2 TrackDefinition, register a track, or touch rendering.  The
 * legacy editor calls the item-position field `boxes`; v2's document shape
 * calls the same authored progress values `items`.  `boxes` is accepted only
 * as that explicit input alias and is emitted canonically as `items`.
 */

export const CUSTOM_TRACK_SCHEMA_VERSION = 1 as const;
export const CUSTOM_TRACK_PREVIEW_KEY = "__editor__" as const;
export const CUSTOM_TRACK_ID_PATTERN = /^c_[a-z0-9_]{1,24}$/;

const DEFAULT_THEME_KEY = "bartop";
const MAX_POINT_COORDINATE = 6_000;
const MAX_NAME_LENGTH = 30;
const MAX_FEATURES = 6;
const MAX_ITEMS = 12;
const PROGRESS_MIN = 0;
const PROGRESS_MAX = 1;
const TOKEN_PATTERN = /^[a-z]{3,15}$/;
const TINT_KEYS = ["kerb", "line", "glow"] as const;
const TINT_KEY_SET = new Set<string>(TINT_KEYS);

export type CustomTrackId = `c_${string}`;
export type CustomTrackPoint = readonly [number, number];

export interface CustomTrackTunnel {
  readonly u0: number;
  readonly u1: number;
}

export interface CustomTrackTint {
  readonly kerb?: string;
  readonly line?: string;
  readonly glow?: string;
}

/**
 * Canonical normalized definition.  `items` is the progress-position list
 * that the v1 editor serialized as `boxes`.
 */
export interface CustomTrackDefinition {
  readonly name: string;
  readonly raw: readonly CustomTrackPoint[];
  readonly width: number;
  readonly themeKey: string;
  readonly floorKey?: string;
  readonly propsKey?: string;
  readonly jumps?: readonly number[];
  readonly tunnels?: readonly CustomTrackTunnel[];
  readonly items?: readonly number[];
  readonly dips?: readonly number[];
  readonly tint?: CustomTrackTint;
  readonly enabled: boolean;
}

/** Input shape accepted from the legacy editor or a future v2 editor. */
export interface CustomTrackDefinitionInput {
  readonly name: string;
  readonly raw: readonly CustomTrackPoint[];
  readonly width: number;
  readonly themeKey?: string;
  readonly floorKey?: string;
  readonly propsKey?: string;
  readonly jumps?: readonly number[];
  readonly tunnels?: readonly CustomTrackTunnel[];
  /** Canonical v2 spelling for legacy `boxes`. */
  readonly items?: readonly number[];
  /** Legacy editor/server spelling; mutually exclusive with `items`. */
  readonly boxes?: readonly number[];
  readonly dips?: readonly number[];
  readonly tint?: CustomTrackTint;
  readonly enabled?: boolean;
}

export interface CustomTrackSavedDocument {
  readonly schemaVersion: typeof CUSTOM_TRACK_SCHEMA_VERSION;
  readonly key: CustomTrackId;
  readonly def: CustomTrackDefinition;
}

export interface CustomTrackPreviewDocument {
  readonly schemaVersion: typeof CUSTOM_TRACK_SCHEMA_VERSION;
  readonly key: typeof CUSTOM_TRACK_PREVIEW_KEY;
  readonly def: CustomTrackDefinition;
  /** Legacy editor timestamp (`Date.now()`), retained for preview TTL checks. */
  readonly t: number;
}

export type CustomTrackDocument = CustomTrackSavedDocument | CustomTrackPreviewDocument;

export type CustomTrackValidationResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: string };

export class CustomTrackValidationError extends Error {
  readonly path: string;

  constructor(path: string, message: string) {
    super(`${path}: ${message}`);
    this.name = "CustomTrackValidationError";
    this.path = path;
  }
}

function fail(path: string, message: string): never {
  throw new CustomTrackValidationError(path, message);
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  try {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}

function recordAt(value: unknown, path: string): Record<string, unknown> {
  if (!isPlainRecord(value)) fail(path, "expected a plain object");
  return value;
}

function assertAllowedKeys(record: Record<string, unknown>, allowed: readonly string[], path: string): void {
  const allowedSet = new Set(allowed);
  for (const key of Object.keys(record)) {
    if (!allowedSet.has(key)) fail(`${path}.${key}`, "unknown field");
  }
}

function assertDenseArray(value: readonly unknown[], path: string): void {
  for (let index = 0; index < value.length; index += 1) {
    if (!Object.prototype.hasOwnProperty.call(value, index)) fail(`${path}[${index}]`, "sparse arrays are not allowed");
  }
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function finiteNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) fail(path, "expected a finite number");
  return value;
}

function boundedProgress(value: unknown, path: string): number {
  const number = finiteNumber(value, path);
  if (number < PROGRESS_MIN || number >= PROGRESS_MAX) fail(path, "expected a progress value in [0, 1)");
  return number;
}

/**
 * Legacy `nearU(...).toFixed(3)` can produce exactly 1 at the end of a long
 * layout.  The v1 sampler wraps that endpoint to the start of the track, so
 * item/dip positions and tunnel endpoints retain the closed [0, 1] input
 * boundary but have one canonical representation in the normalized document.
 */
function boundedWrappedProgress(value: unknown, path: string): number {
  const number = finiteNumber(value, path);
  if (number < PROGRESS_MIN || number > PROGRESS_MAX) fail(path, "expected a progress value in [0, 1]");
  return number === PROGRESS_MAX ? PROGRESS_MIN : number;
}

function normalizeName(value: unknown): string {
  if (typeof value !== "string") fail("def.name", "expected a string");
  if (value.length > MAX_NAME_LENGTH) fail("def.name", `must be at most ${MAX_NAME_LENGTH} characters`);
  const name = value.trim();
  if (!name) fail("def.name", "must not be empty");
  if (/[\u0000-\u001f\u007f]/u.test(name)) fail("def.name", "must not contain control characters");
  return name;
}

function normalizeToken(value: unknown, path: string, defaultValue?: string): string | undefined {
  if (value === "") return defaultValue;
  if (value === undefined) fail(path, "must be omitted rather than undefined");
  if (typeof value !== "string" || !TOKEN_PATTERN.test(value)) {
    fail(path, "expected 3-15 lowercase letters or an empty string");
  }
  return value;
}

function normalizeTint(value: unknown): CustomTrackTint {
  const tint = recordAt(value, "def.tint");
  assertAllowedKeys(tint, TINT_KEYS, "def.tint");
  const normalized: { kerb?: string; line?: string; glow?: string } = {};
  for (const key of Object.keys(tint)) {
    if (!TINT_KEY_SET.has(key)) fail(`def.tint.${key}`, "unknown tint field");
    const colour = tint[key];
    if (typeof colour !== "string" || !/^#[0-9a-fA-F]{6}$/u.test(colour)) {
      fail(`def.tint.${key}`, "expected a #rrggbb colour");
    }
    normalized[key as keyof typeof normalized] = colour;
  }
  return normalized;
}

function normalizePoint(value: unknown, index: number): CustomTrackPoint {
  const path = `def.raw[${index}]`;
  if (!Array.isArray(value) || value.length !== 2 || Object.keys(value).length !== 2) {
    fail(path, "expected exactly two coordinate values");
  }
  const x = finiteNumber(value[0], `${path}[0]`);
  const y = finiteNumber(value[1], `${path}[1]`);
  if (Math.abs(x) > MAX_POINT_COORDINATE || Math.abs(y) > MAX_POINT_COORDINATE) {
    fail(path, `coordinates must be within ±${MAX_POINT_COORDINATE}`);
  }
  return [x, y];
}

function normalizeRaw(value: unknown): CustomTrackPoint[] {
  if (!Array.isArray(value)) fail("def.raw", "expected an array");
  if (value.length < 8 || value.length > 96) fail("def.raw", "expected 8-96 control points");
  assertDenseArray(value, "def.raw");
  return value.map((point, index) => normalizePoint(point, index));
}

function normalizeProgressList(value: unknown, path: string, max: number): number[] {
  if (!Array.isArray(value)) fail(path, "expected an array");
  if (value.length > max) fail(path, `must contain at most ${max} values`);
  assertDenseArray(value, path);
  return value.map((entry, index) => boundedProgress(entry, `${path}[${index}]`));
}

function normalizeWrappedProgressList(value: unknown, path: string, max: number): number[] {
  if (!Array.isArray(value)) fail(path, "expected an array");
  if (value.length > max) fail(path, `must contain at most ${max} values`);
  assertDenseArray(value, path);
  return value.map((entry, index) => boundedWrappedProgress(entry, `${path}[${index}]`));
}

function normalizeTunnels(value: unknown): CustomTrackTunnel[] {
  if (!Array.isArray(value)) fail("def.tunnels", "expected an array");
  if (value.length > MAX_FEATURES) fail("def.tunnels", `must contain at most ${MAX_FEATURES} tunnels`);
  assertDenseArray(value, "def.tunnels");
  return value.map((entry, index) => {
    const path = `def.tunnels[${index}]`;
    const tunnel = recordAt(entry, path);
    assertAllowedKeys(tunnel, ["u0", "u1"], path);
    const u0 = boundedWrappedProgress(tunnel.u0, `${path}.u0`);
    const u1 = boundedWrappedProgress(tunnel.u1, `${path}.u1`);
    if (u0 === u1) fail(path, "span must have non-zero length");
    return { u0, u1 };
  });
}

/** Normalize and clone one editor/server custom-track definition. */
export function normalizeCustomTrackDefinition(value: unknown): CustomTrackDefinition {
  const def = recordAt(value, "def");
  assertAllowedKeys(def, [
    "name", "raw", "width", "themeKey", "floorKey", "propsKey", "jumps", "tunnels",
    "items", "boxes", "dips", "tint", "enabled",
  ], "def");

  const name = normalizeName(def.name);
  const raw = normalizeRaw(def.raw);
  const width = finiteNumber(def.width, "def.width");
  if (width < 55 || width > 130) fail("def.width", "expected a value in [55, 130]");

  const themeKey = hasOwn(def, "themeKey")
    ? normalizeToken(def.themeKey, "def.themeKey", DEFAULT_THEME_KEY) ?? DEFAULT_THEME_KEY
    : DEFAULT_THEME_KEY;
  const floorKey = hasOwn(def, "floorKey") ? normalizeToken(def.floorKey, "def.floorKey") : undefined;
  const propsKey = hasOwn(def, "propsKey") ? normalizeToken(def.propsKey, "def.propsKey") : undefined;

  const jumps = hasOwn(def, "jumps") ? normalizeProgressList(def.jumps, "def.jumps", MAX_FEATURES) : undefined;
  const tunnels = hasOwn(def, "tunnels") ? normalizeTunnels(def.tunnels) : undefined;

  const hasItems = hasOwn(def, "items");
  const hasBoxes = hasOwn(def, "boxes");
  if (hasItems && hasBoxes) fail("def", "use either items or legacy boxes, not both");
  const items = hasItems
    ? normalizeWrappedProgressList(def.items, "def.items", MAX_ITEMS)
    : hasBoxes
      ? normalizeWrappedProgressList(def.boxes, "def.boxes", MAX_ITEMS)
      : undefined;

  const dips = hasOwn(def, "dips") ? normalizeWrappedProgressList(def.dips, "def.dips", MAX_FEATURES) : undefined;
  const tint = hasOwn(def, "tint") ? normalizeTint(def.tint) : undefined;
  const enabled = hasOwn(def, "enabled")
    ? typeof def.enabled === "boolean" ? def.enabled : fail("def.enabled", "expected a boolean")
    : true;

  const normalized: {
    name: string;
    raw: CustomTrackPoint[];
    width: number;
    themeKey: string;
    floorKey?: string;
    propsKey?: string;
    jumps?: number[];
    tunnels?: CustomTrackTunnel[];
    items?: number[];
    dips?: number[];
    tint?: CustomTrackTint;
    enabled: boolean;
  } = { name, raw, width, themeKey, enabled };
  if (floorKey !== undefined) normalized.floorKey = floorKey;
  if (propsKey !== undefined) normalized.propsKey = propsKey;
  if (jumps !== undefined) normalized.jumps = jumps;
  if (tunnels !== undefined) normalized.tunnels = tunnels;
  if (items !== undefined) normalized.items = items;
  if (dips !== undefined) normalized.dips = dips;
  if (tint !== undefined) normalized.tint = tint;
  return normalized;
}

/** Validate without throwing; successful validation still returns a detached clone. */
export function validateCustomTrackDefinition(value: unknown): CustomTrackValidationResult<CustomTrackDefinition> {
  try {
    return { ok: true, value: normalizeCustomTrackDefinition(value) };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "invalid custom-track definition" };
  }
}

export function isCustomTrackDefinition(value: unknown): boolean {
  return validateCustomTrackDefinition(value).ok;
}

export function tryNormalizeCustomTrackDefinition(value: unknown): CustomTrackDefinition | null {
  const result = validateCustomTrackDefinition(value);
  return result.ok ? result.value : null;
}

/** Strict custom-track id used by saved definitions; preview uses its reserved key. */
export function normalizeCustomTrackId(value: unknown): CustomTrackId {
  if (typeof value !== "string" || !CUSTOM_TRACK_ID_PATTERN.test(value)) {
    fail("key", "expected c_ followed by 1-24 lowercase letters, digits, or underscores");
  }
  return value as CustomTrackId;
}

function normalizeSchemaVersion(document: Record<string, unknown>): typeof CUSTOM_TRACK_SCHEMA_VERSION {
  if (!hasOwn(document, "schemaVersion")) return CUSTOM_TRACK_SCHEMA_VERSION;
  if (document.schemaVersion !== CUSTOM_TRACK_SCHEMA_VERSION) {
    fail("schemaVersion", `expected ${CUSTOM_TRACK_SCHEMA_VERSION}`);
  }
  return CUSTOM_TRACK_SCHEMA_VERSION;
}

/** Normalize a durable `{ key, def }` saved document. */
export function normalizeCustomTrackSavedDocument(value: unknown): CustomTrackSavedDocument {
  const document = recordAt(value, "document");
  assertAllowedKeys(document, ["schemaVersion", "key", "def"], "document");
  normalizeSchemaVersion(document);
  const key = normalizeCustomTrackId(document.key);
  return { schemaVersion: CUSTOM_TRACK_SCHEMA_VERSION, key, def: normalizeCustomTrackDefinition(document.def) };
}

export function validateCustomTrackSavedDocument(value: unknown): CustomTrackValidationResult<CustomTrackSavedDocument> {
  try {
    return { ok: true, value: normalizeCustomTrackSavedDocument(value) };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "invalid saved custom-track document" };
  }
}

export function isCustomTrackSavedDocument(value: unknown): boolean {
  return validateCustomTrackSavedDocument(value).ok;
}

/** Normalize the legacy editor's `{ key: "__editor__", def, t }` preview payload. */
export function normalizeCustomTrackPreviewDocument(value: unknown): CustomTrackPreviewDocument {
  const document = recordAt(value, "document");
  assertAllowedKeys(document, ["schemaVersion", "key", "def", "t"], "document");
  normalizeSchemaVersion(document);
  if (document.key !== CUSTOM_TRACK_PREVIEW_KEY) fail("document.key", `expected ${CUSTOM_TRACK_PREVIEW_KEY}`);
  const timestamp = finiteNumber(document.t, "document.t");
  if (!Number.isSafeInteger(timestamp) || timestamp <= 0) fail("document.t", "expected a positive safe integer timestamp");
  return {
    schemaVersion: CUSTOM_TRACK_SCHEMA_VERSION,
    key: CUSTOM_TRACK_PREVIEW_KEY,
    def: normalizeCustomTrackDefinition(document.def),
    t: timestamp,
  };
}

export function validateCustomTrackPreviewDocument(value: unknown): CustomTrackValidationResult<CustomTrackPreviewDocument> {
  try {
    return { ok: true, value: normalizeCustomTrackPreviewDocument(value) };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "invalid preview custom-track document" };
  }
}

export function isCustomTrackPreviewDocument(value: unknown): boolean {
  return validateCustomTrackPreviewDocument(value).ok;
}

/** Discriminate a saved or reserved editor-preview document by its key. */
export function normalizeCustomTrackDocument(value: unknown): CustomTrackDocument {
  const document = recordAt(value, "document");
  return document.key === CUSTOM_TRACK_PREVIEW_KEY
    ? normalizeCustomTrackPreviewDocument(document)
    : normalizeCustomTrackSavedDocument(document);
}

export function validateCustomTrackDocument(value: unknown): CustomTrackValidationResult<CustomTrackDocument> {
  try {
    return { ok: true, value: normalizeCustomTrackDocument(value) };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "invalid custom-track document" };
  }
}

export function isCustomTrackDocument(value: unknown): boolean {
  return validateCustomTrackDocument(value).ok;
}
